import { z } from 'zod';

/**
 * How long a product key may be. Generous, because it is operator-authored
 * shorthand (`premium.monthly`, `gold_12m`) rather than a display string.
 */
export const PRODUCT_KEY_MAX_LENGTH = 120;

/**
 * A subscription product's stable identifier — the name every other part of the
 * platform refers to a plan by.
 *
 * It is **the key, not the id**: `billing.directPrices` is keyed by it, discount
 * rules are scoped to it, and store product ids map to it. All three want a
 * value an operator can read in a settings map, and none of them may hold a
 * uuid a human cannot check.
 *
 * ## Why a string and not an enum
 *
 * Products are created by staff at runtime, so the set cannot be coded. A key
 * that stops existing simply **stops matching** — the correct behaviour for a
 * withdrawn product, and the reason nothing joins through it to fail loudly.
 *
 * ## Why it lives in `billing`
 *
 * It was declared in `courses/discount-rule` while billing did not exist, with
 * a docblock saying so. Billing owns the products, so it owns the key (U3);
 * courses imports it from here, which is the direction the dependency actually
 * runs.
 */
export const ProductKeySchema = z.string().trim().min(1).max(PRODUCT_KEY_MAX_LENGTH);

export type ProductKey = z.infer<typeof ProductKeySchema>;
